import React, { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus, Trash2, X, CheckCircle2, CalendarDays, Loader2, Pencil, Filter,
} from "lucide-react";
import adminApi from "./adminApi";

const emptyForm = { title: "", description: "", event_date: "" };

function formatDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function ManageEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ── Year filter ──────────────────────────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const [years, setYears] = useState([]);
  const [yearFilter, setYearFilter] = useState(String(currentYear)); // defaults to current year; "" = all years

  // ── Pagination ───────────────────────────────────────────────────────────
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });

  // ── Title autocomplete source ───────────────────────────────────────────
  const [titleOptions, setTitleOptions] = useState([]);
  const [showTitleSuggestions, setShowTitleSuggestions] = useState(false);
  const titleFieldRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = "add new"
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchEvents = async (year = yearFilter, pageNum = page) => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.get("/admin/events", {
        params: {
          ...(year ? { year } : {}),
          page: pageNum,
          limit: PAGE_SIZE,
        },
      });
      setEvents(res.data.events || []);
      if (res.data.pagination) {
        setPagination(res.data.pagination);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Could not load events.");
    } finally {
      setLoading(false);
    }
  };

  const fetchMeta = async () => {
    try {
      const res = await adminApi.get("/admin/events/meta/filters");
      setYears(res.data.years || []);
      setTitleOptions(res.data.titles || []);
    } catch (err) {
      // Non-fatal — filter/autocomplete just won't have data
      console.error("Could not load event filters:", err);
    }
  };

  useEffect(() => {
    fetchEvents(yearFilter, 1);
    fetchMeta();
  }, []);

  // Changing the year filter always goes back to page 1
  useEffect(() => {
    setPage(1);
    fetchEvents(yearFilter, 1);
    setSelected([]);
    // eslint-disable-next-line
  }, [yearFilter]);

  // Re-fetch when the page changes (but not on the very first mount, already handled above)
  const isFirstPageRun = useRef(true);
  useEffect(() => {
    if (isFirstPageRun.current) {
      isFirstPageRun.current = false;
      return;
    }
    fetchEvents(yearFilter, page);
    setSelected([]);
    // eslint-disable-next-line
  }, [page]);

  // ── Selection ────────────────────────────────────────────────────────────
  const toggleSelectAll = () => {
    setSelected(selected.length === events.length ? [] : events.map((e) => e.id));
  };
  const toggleSelectOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // ── Bulk delete ──────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    const ok = window.confirm(
      `Are you sure you want to delete ${selected.length} selected event(s)? This cannot be undone.`
    );
    if (!ok) return;

    setBulkDeleting(true);
    try {
      await adminApi.post("/admin/events/bulk-delete", { ids: selected });
      showToast("success", `${selected.length} event(s) deleted.`);
      const deletingAllOnPage = selected.length >= events.length;
      const nextPage = deletingAllOnPage && page > 1 ? page - 1 : page;
      setSelected([]);
      if (nextPage === page) {
        fetchEvents(yearFilter, nextPage); // page number unchanged, so the page-effect won't refetch
      } else {
        setPage(nextPage); // triggers the page-change effect to refetch
      }
      fetchMeta();
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not delete selected events.");
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Single delete ────────────────────────────────────────────────────────
  const handleDeleteOne = async (item, e) => {
    e.stopPropagation();
    const ok = window.confirm(`Delete "${item.title}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await adminApi.delete(`/admin/events/${item.id}`);
      showToast("success", "Event deleted.");
      const nextPage = events.length === 1 && page > 1 ? page - 1 : page;
      if (nextPage === page) {
        fetchEvents(yearFilter, nextPage);
      } else {
        setPage(nextPage);
      }
      fetchMeta();
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not delete event.");
    }
  };

  // ── Add / Edit modal ─────────────────────────────────────────────────────
  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      description: item.description || "",
      // event_date from MySQL DATE/DATETIME comes as "YYYY-MM-DD..." — trim to date input format
      event_date: item.event_date ? String(item.event_date).slice(0, 10) : "",
    });
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setShowTitleSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.title.trim()) {
      setFormError("Event Title is required.");
      return;
    }
    if (!form.event_date) {
      setFormError("Event Date is required.");
      return;
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      event_date: form.event_date,
    };

    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put(`/admin/events/${editingId}`, payload);
        showToast("success", "Event updated.");
      } else {
        await adminApi.post("/admin/events", payload);
        showToast("success", "Event added.");
      }
      setShowModal(false);
      fetchEvents(yearFilter, page);
      fetchMeta(); // refresh title suggestions + years in case a new one was added
    } catch (err) {
      setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Title autocomplete helpers ──────────────────────────────────────────
  const filteredTitleSuggestions = form.title.trim()
    ? titleOptions.filter((t) =>
        t.toLowerCase().includes(form.title.trim().toLowerCase())
      )
    : titleOptions;

  const exactTitleMatch = titleOptions.some(
    (t) => t.toLowerCase() === form.title.trim().toLowerCase()
  );

  const handleTitleChange = (value) => {
    setForm((f) => ({ ...f, title: value }));
    setShowTitleSuggestions(true);
  };

  const selectTitleSuggestion = (value) => {
    setForm((f) => ({ ...f, title: value }));
    setShowTitleSuggestions(false);
  };

  const allSelected = events.length > 0 && selected.length === events.length;

  // Always include the current year as a selectable option, even if there
  // are no events for it yet in the fetched `years` list.
  const yearOptions = Array.from(new Set([currentYear, ...years])).sort(
    (a, b) => b - a
  );

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="relative flex flex-wrap items-center justify-between gap-3 shrink-0 mb-2">
        {/* Left: title */}
        <div>
          <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage Calendar Events
          </h3>
        </div>

        {/* Middle: year filter, absolutely centered so it never disturbs left/right layout */}
        <div className="w-full sm:w-auto order-3 sm:order-none sm:absolute sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 flex justify-center">
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5">
            <Filter size={13} className="text-slate-400" />
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="text-[13px] outline-none bg-transparent text-slate-700 cursor-pointer"
            >
              <option value="">All Years</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {selected.length > 0 && (
              <motion.button
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex items-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 text-[13px] font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-60"
              >
                {bulkDeleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                Delete Selected ({selected.length})
              </motion.button>
            )}
          </AnimatePresence>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
          >
            <Plus size={15} />
            Add New Event
          </button>
        </div>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading events...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : events.length === 0 ? (
          <div className="py-16 text-center">
            <CalendarDays size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">
              {yearFilter
                ? `No events found for ${yearFilter}.`
                : `No events yet — click "Add New Event" to create one.`}
            </p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 shadow-md">
                <tr className="text-left text-base">
                  <th className="th-colorful pl-3 pr-2 py-3 w-8 border-b-2 border-r normal-case">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                    />
                  </th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">Title</th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">Description</th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">Event Date</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {events.map((ev, i) => (
                    <motion.tr
                      key={ev.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.02 }}
                      onClick={() => openEditModal(ev)}
                      className={`cursor-pointer hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200 ${
                        selected.includes(ev.id) ? "bg-[#0EA5A4]/15" : ""
                      }`}
                    >
                      <td className="pl-3 pr-2 py-2 border-r border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.includes(ev.id)}
                          onChange={() => toggleSelectOne(ev.id)}
                          className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2 text-slate-800 font-medium border-r border-slate-200">
                        {ev.title}
                      </td>
                      <td className="px-3 py-2 text-slate-500 max-w-[280px] truncate border-r border-slate-200" title={ev.description}>
                        {ev.description || "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-600 border-r border-slate-200">
                        <span className="inline-flex items-center gap-1.5">
                          <CalendarDays size={13} className="text-[#0EA5A4]" />
                          {formatDate(ev.event_date)}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && pagination.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 mt-3 text-[13px] text-slate-500">
          <div>
            Showing{" "}
            <span className="font-medium text-slate-700">
              {(pagination.page - 1) * pagination.limit + 1}
            </span>
            {"–"}
            <span className="font-medium text-slate-700">
              {Math.min(pagination.page * pagination.limit, pagination.total)}
            </span>{" "}
            of <span className="font-medium text-slate-700">{pagination.total}</span> events
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={pagination.page <= 1}
              className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
            >
              Prev
            </button>

            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter((p) => {
                // Show first, last, current, and neighbors of current; collapse the rest
                return (
                  p === 1 ||
                  p === pagination.totalPages ||
                  Math.abs(p - pagination.page) <= 1
                );
              })
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("ellipsis-" + p);
                acc.push(p);
                return acc;
              }, [])
              .map((p) =>
                typeof p === "string" ? (
                  <span key={p} className="px-1.5 text-slate-300">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`min-w-[32px] px-2.5 py-1.5 rounded-lg border text-[13px] transition ${
                      p === pagination.page
                        ? "bg-[#0EA5A4] border-[#0EA5A4] text-white font-medium"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}

            <button
              onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
              disabled={pagination.page >= pagination.totalPages}
              className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h4 className="font-display font-semibold text-[#0B2545]">
                  {editingId ? "Edit Event" : "Add New Event"}
                </h4>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-700">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                {/* Title with autocomplete */}
                <div className="relative" ref={titleFieldRef}>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Event Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    onFocus={() => setShowTitleSuggestions(true)}
                    onBlur={() => {
                      // slight delay so a suggestion click registers before we hide the list
                      setTimeout(() => setShowTitleSuggestions(false), 150);
                    }}
                    autoComplete="off"
                    placeholder="e.g. Durga Puja"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />

                  {(() => {
                    const typed = form.title.trim();
                    if (!showTitleSuggestions || !typed) return null;

                    const isExactMatch = titleOptions.some(
                      (t) => t.toLowerCase() === typed.toLowerCase()
                    );
                    const matches = titleOptions.filter((t) =>
                      t.toLowerCase().includes(typed.toLowerCase())
                    );

                    // Nothing to show at all (shouldn't normally happen since
                    // the "add new" row covers every non-exact-match case,
                    // but guards against an empty render either way).
                    if (isExactMatch && matches.length === 0) return null;

                    return (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                        {!isExactMatch && (
                          <div
                            onMouseDown={() => setShowTitleSuggestions(false)}
                            className="px-3 py-2 text-[13px] text-[#0B7E7D] bg-[#0EA5A4]/5 border-b border-slate-100 cursor-pointer hover:bg-[#0EA5A4]/10 flex items-center gap-1.5"
                          >
                            <Plus size={12} className="shrink-0" />
                            <span>
                              Add "<span className="font-medium">{typed}</span>" as new event
                            </span>
                          </div>
                        )}
                        {matches.map((t) => (
                          <div
                            key={t}
                            onMouseDown={() => selectTitleSuggestion(t)}
                            className="px-3 py-2 text-sm text-slate-700 hover:bg-[#0EA5A4]/10 cursor-pointer"
                          >
                            {t}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Description
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Short description of this event..."
                    rows={3}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Event Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />
                </div>

                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {formError}
                  </p>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg py-2.5 transition disabled:opacity-60"
                  >
                    {saving ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : editingId ? (
                      <Pencil size={15} />
                    ) : (
                      <Plus size={15} />
                    )}
                    {saving ? "Saving..." : editingId ? "Update Event" : "Submit"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
